/**
 * Build an in-app deep link for a notification row.
 *
 * Resolution rules:
 *
 *   • Buyout / expiry (seat-related)
 *       → `/board/{boardId}` with `?seat=NNN` when a single seat is
 *         identifiable. The lobby reads `seat` and pins the matching
 *         tile's tooltip so the viewer lands directly on the affected
 *         seat instead of a blank board.
 *       → Bulk buyouts drop the `seat` param — there is no single tile
 *         to focus, so we surface the board itself.
 *       → If the `pool_id` isn't a lobby-routable grid id (e.g. a
 *         standalone coin pool), fall back to the legacy `href` stored
 *         on the row (`/coin/{poolId}` etc.) so the link still works.
 *
 *   • Chat mention
 *       → `/board/{boardId}` with `?chat_msg=ID` when a `message_id`
 *         is present. The lobby chat panel scrolls to + briefly
 *         highlights that message on mount.
 *       → Reserved chat rooms without a lobby page (`megapot-grid`)
 *         fall back to the legacy `/chat?room=…` URL.
 *
 * The helper is intentionally synchronous and pure so it can be reused
 * by both the live `NotificationsDropdown` (lobby) and the legacy
 * `NotificationBell` (chromed routes) without dragging the boards
 * fetch into the bell render path.
 */

import type { Notification } from '@/hooks/use-notifications';
import type { NotificationRecord } from '@/lib/db/notifications';
import {
  isLiveChatRoomId,
  isReservedChatRoomId,
} from '@/lib/chat-rooms';
import { lobbyPathForBoard, lobbyBoardIdFromGridId } from '@/lib/lobby-routes';

/** Lobby URL query param that pins a seat tooltip on mount. */
export const SEAT_FOCUS_PARAM = 'seat';
/** Lobby URL query param that scrolls the chat panel to a specific message. */
export const CHAT_MSG_PARAM = 'chat_msg';

/** Subset of the notification shape this helper actually reads — keeps
 *  callers from having to satisfy the full row type and lets server-side
 *  call sites share the same logic. */
type NotificationLike = Pick<
  Notification,
  | 'type'
  | 'pool_id'
  | 'seat_index'
  | 'seat_indices'
  | 'href'
  | 'room'
  | 'message_id'
>;

/** A grid-style pool id has the `0x<40 hex>-<tokenId>` shape used by the
 *  live chat room vocabulary. Reused here because lobby boards share that
 *  exact id format — anything that matches can be routed via `/board/…`. */
function isLobbyRoutableGridId(id: string | null | undefined): boolean {
  if (!id) return false;
  return isLiveChatRoomId(id);
}

/** Format a 0-based seat index as the 3-digit, 1-based label the lobby
 *  uses everywhere (`017`). Returns `null` when the index is missing or
 *  the row represents a multi-seat buyout. */
function formatSeatParam(n: NotificationLike): string | null {
  if (n.seat_indices && n.seat_indices.length > 1) return null;
  const idx = n.seat_index;
  if (idx == null || idx < 0) return null;
  return String(idx + 1).padStart(3, '0');
}

/**
 * Resolve the lobby board id for a notification's `pool_id` / `room`.
 * Returns `null` when no lobby page can be reached (e.g. coin-only
 * pools, unknown reserved rooms) so callers know to fall back to the
 * legacy `href`.
 */
function resolveLobbyBoardId(n: NotificationLike): string | null {
  if (n.type === 'chat_mention') {
    const room = n.room ?? n.pool_id;
    if (!room) return null;
    if (room === 'boardroom') return 'boardroom';
    if (isLobbyRoutableGridId(room)) return lobbyBoardIdFromGridId(room);
    // Reserved labels like `megapot-grid` map to a UGM grid via env vars
    // that aren't safe to read on the client. Punt to the legacy chat
    // URL instead of guessing.
    if (isReservedChatRoomId(room)) return null;
    return null;
  }
  // Buyout / expiry: `pool_id` is the grid id (composite or boardroom).
  if (!n.pool_id) return null;
  if (isLobbyRoutableGridId(n.pool_id)) return lobbyBoardIdFromGridId(n.pool_id);
  return null;
}

/**
 * Compute the in-app navigation URL for a notification, or `null` when
 * the notification has no actionable destination.
 */
export function notificationHref(n: NotificationLike): string | null {
  if (n.type === 'chat_mention') {
    const boardId = resolveLobbyBoardId(n);
    if (boardId) {
      const base = lobbyPathForBoard(boardId);
      const params = new URLSearchParams();
      if (n.message_id != null) {
        params.set(CHAT_MSG_PARAM, String(n.message_id));
      }
      const qs = params.toString();
      return qs ? `${base}?${qs}` : base;
    }
    // Fall back to whatever the row was originally created with —
    // historically `/chat?room=…`, which still resolves.
    return n.href ?? null;
  }

  // Buyout / expiry — both keyed on `pool_id`.
  const boardId = resolveLobbyBoardId(n);
  if (boardId) {
    const base = lobbyPathForBoard(boardId);
    const seat = formatSeatParam(n);
    return seat ? `${base}?${SEAT_FOCUS_PARAM}=${seat}` : base;
  }

  return n.href ?? null;
}

/** Server-friendly entry point — accepts the raw DB row shape. */
export function notificationHrefForRecord(n: NotificationRecord): string | null {
  return notificationHref(n);
}
